import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../../services/api';
import { KGInstance } from '../../types';
import { extractErrorMessage } from '../../utils/errorHandler';
import NLQPanel from './NLQPanel';
import GraphPlot from './GraphPlot';
import './QueryInterface.css';

interface QueryInterfaceProps {
  kgId: string;
  versionId: string;
  kgName?: string;
}

type RunMode = 'run' | 'explain' | 'profile';
type ResultTab = 'raw' | 'table' | 'plot';

interface QueryResultItem {
  [key: string]: any;
}

interface QueryRun {
  query: string;
  mode: RunMode;
  result: any;
  error: string;
  durationMs: number;
  rowCount: number;
}


function extractResultArray(result: any): QueryResultItem[] {
  try {
    const attrs = result?.data?.attributes ?? result?.attributes ?? result;
    const raw = attrs?.result ?? attrs?.results ?? attrs?.data ?? attrs;
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

// alias kept for readability at call sites
const parseRows = extractResultArray;

function getColumns(rows: QueryResultItem[]): string[] {
  if (!rows.length) return [];
  const cols = new Set<string>();
  rows.slice(0, 20).forEach(r => Object.keys(r).forEach(k => cols.add(k)));
  return Array.from(cols);
}

function renderCell(val: any): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}


const QueryInterface: React.FC<QueryInterfaceProps> = ({ kgId, versionId, kgName }) => {
  const [queryString, setQueryString] = useState(
    'MATCH (n) RETURN labels(n) AS label, count(*) AS count ORDER BY count DESC'
  );
  const [instances, setInstances] = useState<KGInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [instancesLoading, setInstancesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [runMode, setRunMode] = useState<RunMode>('run');
  const [resultTab, setResultTab] = useState<ResultTab>('raw');
  const [run, setRun] = useState<QueryRun | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [nlqCollapsed, setNlqCollapsed] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { loadInstances(); }, [kgId, versionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInstances = async () => {
    setInstancesLoading(true);
    try {
      const response: any = await apiService.listInstances(kgId, versionId);
      if (response.data) {
        const instanceList = response.data.map((item: any) => ({
          instance_id:      item.instance_id      || item.attributes?.instance_id,
          kg_id:            item.kg_id             || item.attributes?.kg_id             || kgId,
          version_id:       item.version_id        || item.attributes?.version_id        || versionId,
          instance_type:    item.instance_type     || item.attributes?.instance_type,
          status:           item.status            || item.attributes?.status,
          is_terminated:    item.is_terminated     ?? item.attributes?.is_terminated     ?? false,
          default_instance: item.default_instance  ?? item.attributes?.default_instance  ?? false,
          created_at:       item.created_at        || item.attributes?.created_at,
          org_id:           item.org_id            || item.attributes?.org_id,
        }));
        const running = instanceList.filter((i: KGInstance) => {
          const s = (i.status || '').toLowerCase();
          return s === 'running' || s === 'active';
        });
        setInstances(running);
        const def = running.find((i: KGInstance) => i.default_instance) || running[0];
        if (def) setSelectedInstance(def.instance_id);
      }
    } catch (err) {
      console.error('Error loading instances:', err);
    } finally {
      setInstancesLoading(false);
    }
  };

  const executeQuery = useCallback(async (qs?: string, mode?: RunMode) => {
    const q = (qs ?? queryString).trim();
    const m = mode ?? runMode;
    if (!q) { setError('Query is required'); return; }
    if (!selectedInstance) { setError('Please select an instance'); return; }

    const queries = m === 'run'
      ? q.split(';').map(s => s.trim()).filter(Boolean)
      : [q];

    const prefixed = queries.map(raw => {
      const upper = raw.toUpperCase();
      if (m === 'explain' && !upper.startsWith('EXPLAIN')) return `EXPLAIN ${raw}`;
      if (m === 'profile' && !upper.startsWith('PROFILE')) return `PROFILE ${raw}`;
      return raw;
    });

    setError('');
    setRun(null);
    setLoading(true);

    const t0 = performance.now();
    try {
      let lastResult: any = null;
      for (const pq of prefixed) {
        lastResult = await apiService.executeSynchronousQuery(kgId, versionId, pq, selectedInstance);
      }
      const durationMs = Math.round(performance.now() - t0);
      const rows = parseRows(lastResult);
      setRun({ query: prefixed.join(';\n'), mode: m, result: lastResult, error: '', durationMs, rowCount: rows.length });
      setResultTab('raw');
    } catch (err: any) {
      const durationMs = Math.round(performance.now() - t0);
      const msg = extractErrorMessage(err, 'Failed to execute query');
      setError(msg);
      setRun({ query: prefixed.join(';\n'), mode: m, result: null, error: msg, durationMs, rowCount: 0 });
    } finally {
      setLoading(false);
    }
  }, [queryString, runMode, selectedInstance, kgId, versionId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      executeQuery();
    }
  };

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  const copyRawJson = () => {
    const text = JSON.stringify(run?.result, null, 2);
    navigator.clipboard.writeText(text).then(() => setCopied(true));
  };

  const rows    = run?.result ? parseRows(run.result) : [];
  const columns = getColumns(rows);
  const hasRows = rows.length > 0;
  const hasResult = run !== null;
  const noInstances = !instancesLoading && instances.length === 0;

  // ── Loading state: instances still fetching ──────────────────────────────────
  if (instancesLoading) {
    return (
      <div className="qi-root">
        <div className="qi-full-center">
          <span className="page-spinner" />
        </div>
      </div>
    );
  }

  // ── Empty state: no running instances ────────────────────────────────────────
  if (noInstances) {
    return (
      <div className="qi-root">
        <div className="qi-full-center">
          <div className="qi-no-inst-icon">⬡</div>
          <div className="qi-no-inst-title">No running instances</div>
          <div className="qi-no-inst-body">
            Go to the <strong>Instances</strong> tab to launch an instance, then come back here to run queries.
          </div>
        </div>
      </div>
    );
  }

  // Format instance option: type · last 10 chars of ID · default flag
  const fmtInst = (i: KGInstance) => {
    const type = i.instance_type || 'instance';
    const tail = i.instance_id.slice(-10);
    return `${type} · …${tail}${i.default_instance ? ' · default' : ''}`;
  };

  // ── Main query UI ─────────────────────────────────────────────────────────────
  return (
    <div className="qi-root">
      {/* ── Top bar: instance + stat only ── */}
      <div className="qi-topbar">
        <div className="qi-instance-wrap">
          <span className="qi-instance-dot" />
          <select
            className="qi-select"
            value={selectedInstance}
            onChange={e => setSelectedInstance(e.target.value)}
          >
            {instances.map(i => (
              <option key={i.instance_id} value={i.instance_id}>
                {fmtInst(i)}
              </option>
            ))}
          </select>
        </div>
        {run && (
          <span className="qi-stat-pill">
            {run.rowCount > 0 ? `${run.rowCount} row${run.rowCount !== 1 ? 's' : ''}` : 'no rows'} · {run.durationMs}ms
          </span>
        )}
      </div>

      {/* ── Two-column body ── */}
      <div className="qi-body">
        {/* LEFT: editor + quick commands */}
        <div className="qi-left">

          {/* Editor panel — fixed height, run button inside header */}
          <div className="qi-panel qi-panel--editor">
            <div className="qi-panel-header">
              <span className="qi-panel-title">Cypher Editor</span>
              <div className="qi-editor-actions">
                {/* Mode pills */}
                <div className="qi-mode-pills">
                  {(['run', 'explain', 'profile'] as RunMode[]).map(m => (
                    <button
                      key={m}
                      className={`qi-mode-pill${runMode === m ? ' qi-mode-pill--active' : ''}${m !== 'run' ? ' qi-mode-pill--disabled' : ''}`}
                      onClick={() => m === 'run' && setRunMode(m)}
                      disabled={m !== 'run'}
                      title={m !== 'run' ? 'Not supported by this API endpoint' : undefined}
                    >
                      {m === 'run' ? 'Run' : m === 'explain' ? 'Explain' : 'Profile'}
                    </button>
                  ))}
                </div>
                {/* Run button */}
                <button
                  className="qi-run-btn"
                  onClick={() => executeQuery()}
                  disabled={loading || !selectedInstance}
                >
                  ▶
                </button>
              </div>
            </div>
            <textarea
              ref={textareaRef}
              className="qi-editor"
              value={queryString}
              onChange={e => setQueryString(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={"MATCH (n) RETURN n LIMIT 10\n\n-- separate multiple queries with ;\n-- Ctrl+Enter or ⌘↵ to run"}
              spellCheck={false}
              autoComplete="off"
              autoCorrect="off"
            />
          </div>

          {/* NLQ panel — replaces Quick Commands */}
          <div className={`qi-panel qi-panel--snippets${nlqCollapsed ? ' qi-panel--snippets-collapsed' : ''}`}>
            <NLQPanel
              kgId={kgId}
              kgName={kgName || kgId}
              versionId={versionId}
              onQuerySuggested={(cypher) => {
                setQueryString(cypher);
                textareaRef.current?.focus();
              }}
              onCollapsedChange={setNlqCollapsed}
            />
          </div>
        </div>

        {/* RIGHT: results */}
        <div className="qi-right">
          <div className="qi-panel qi-panel--results">
            {loading && (
              <div className="qi-running">
                <span className="page-spinner" />
              </div>
            )}

            {!loading && !hasResult && !error && (
              <div className="qi-empty">
                <div className="qi-empty-icon">⌗</div>
                <div className="qi-empty-title">No results yet</div>
                <div className="qi-empty-sub">Run a query to see results here</div>
              </div>
            )}

            {!loading && error && (
              <div className="qi-error-box">
                <span className="qi-error-icon">⚠</span>
                <pre className="qi-error-text">{error}</pre>
              </div>
            )}

            {!loading && hasResult && !error && (run?.mode === 'explain' || run?.mode === 'profile') && !hasRows && (
              <div className="qi-plan-notice">
                <span className="qi-plan-notice-icon">ℹ</span>
                <div>
                  <strong>{run.mode === 'explain' ? 'EXPLAIN' : 'PROFILE'}</strong> was sent to Neo4j successfully, but the query plan is not returned by this API endpoint — only data rows are exposed. Switch to <strong>Run</strong> mode to see results, or use the Neo4j Browser for full plan output.
                </div>
              </div>
            )}

            {!loading && hasResult && !error && !(( run?.mode === 'explain' || run?.mode === 'profile') && !hasRows) && (
              <>
                <div className="qi-result-tabs">
                  {(['raw', 'table', 'plot'] as ResultTab[]).map(t => (
                    <button
                      key={t}
                      className={`qi-result-tab${resultTab === t ? ' qi-result-tab--active' : ''}`}
                      onClick={() => setResultTab(t)}
                    >
                      {t === 'table' ? 'Table' : t === 'raw' ? 'Raw JSON' : 'Plot'}
                    </button>
                  ))}
                  <div className="qi-result-tabs-spacer" />
                  {resultTab === 'raw' && (
                    <button className="qi-copy-btn" onClick={copyRawJson} title="Copy to clipboard">
                      {copied ? '✓ Copied' : '⎘ Copy'}
                    </button>
                  )}
                  <span className={`qi-mode-badge qi-mode-badge--${run?.mode}`}>
                    {run?.mode?.toUpperCase()}
                  </span>
                </div>

                {/* Scrollable body — table & raw only */}
                {resultTab !== 'plot' && (
                  <div className="qi-result-body">
                    {resultTab === 'table' && (
                      hasRows ? (
                        <div className="qi-table-wrap">
                          <table className="qi-table">
                            <thead>
                              <tr>{columns.map(c => <th key={c}>{c}</th>)}</tr>
                            </thead>
                            <tbody>
                              {rows.map((row, ri) => (
                                <tr key={`${ri}-${columns.map(c => row[c]).join('|')}`}>
                                  {columns.map(c => (
                                    <td key={c} title={renderCell(row[c])}>
                                      {renderCell(row[c])}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="qi-empty qi-empty--sm">
                          <div className="qi-empty-sub">Query returned no rows</div>
                        </div>
                      )
                    )}

                    {resultTab === 'raw' && (
                      <pre className="qi-raw">{JSON.stringify(run?.result, null, 2)}</pre>
                    )}
                  </div>
                )}

                {/* Plot — fills remaining panel height directly, no scroll wrapper */}
                {resultTab === 'plot' && (
                  <div className="qi-plot-body">
                    <GraphPlot rows={rows} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueryInterface;
